import React from 'react'

import { ListCard } from 'component-library/cards/ListCard/ListCard'
import { HeroCard } from 'component-library/collections-new/Oscar/components/HeroCard'
import {
    SectionHeader,
    Props as SectionHeaderProps,
} from 'component-library/section-header/SectionHeader/SectionHeader'

import {
    StyledOscar,
    StyledHeader,
    StyledGridList,
} from 'component-library/collections-new/Oscar/Oscar.styled'
import { ImpressionAvailable } from 'component-library/__helpers/impression-available-helper'
import { CardSwitcher } from 'component-library/__helpers/CardSwitcher'
import { invertMaybeLoadedItems } from '../helpers/loading'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import { OverrideThemeSection } from 'component-library/__helpers/override-theme-section'
import { useProduct } from "common/hooks/useProduct"
import { CollectionEvent, createCollectionAvailableEvent } from 'common/events/collection-events'
import { CardItem } from 'common/client-data-types/card-types'
import { Product } from 'component-library/__product'
import { Section } from 'component-library/__styling/settings/sections'
import { ThemeMargins } from 'component-library/__styling/settings/metrics'

export interface Props {
    className?: string
    sectionHeader?: SectionHeaderProps
    section?: Section
    verticalSpacing?: keyof ThemeMargins
    heroCardType: 'landscape-stacked' | 'portrait'
    disableImageLazyLoad?: boolean
    truncateListCards?: boolean
    hasSeparators?: boolean
    onEvent: (event: CollectionEvent) => void
    expectedCards: number
    items: MaybeLoaded<CardItem[]>
}

export const Oscar: React.FC<Props> = props => {
    const {
        className,
        sectionHeader,
        onEvent,
        verticalSpacing,
        section,
        truncateListCards,
        disableImageLazyLoad,
        hasSeparators,
        expectedCards,
    } = props

    const items = invertMaybeLoadedItems(props.items, expectedCards)
    const product = useProduct()

    const removeMargins = product !== 'perthnow'

    const heroCardPadding = product === Product.PerthNow ? true : false

    const renderOscar = () => (
        <ImpressionAvailable
            loading={!props.items.loaded}
            available={() => {
                if (!props.items.loaded) {
                    console.warn('Available should never be called when loading is true')
                    return
                }
                props.onEvent(createCollectionAvailableEvent(props.items.result, 'Oscar'))
            }}
        >
            {ref => (
                <StyledOscar ref={ref} className={className} verticalSpacing={verticalSpacing}>
                    {sectionHeader && items && (
                        <StyledHeader>
                            <SectionHeader
                                heading={sectionHeader.heading}
                                section={sectionHeader.section}
                                hasMargin={sectionHeader.hasMargin}
                                headingUrl={sectionHeader.headingUrl}
                                masthead={sectionHeader.masthead}
                                navLinks={sectionHeader.navLinks}
                                onClick={sectionHeader.onClick}
                                canGrow={true}
                            />
                        </StyledHeader>
                    )}
                    {items &&
                        items
                            .slice(0, 1)
                            .map((item, index) => (
                                <CardSwitcher
                                    key={index}
                                    item={item}
                                    cardContext="oscar-hero"
                                    cardNumber={1}
                                    publicationCard={publicationItem => (
                                        <HeroCard
                                            key={index}
                                            item={publicationItem}
                                            heroCardType={props.heroCardType || 'landscape-stacked'}
                                            mediaMode={'default'}
                                            hasPadding={heroCardPadding}
                                            onEvent={props.onEvent}
                                            disableImageLazyLoad={disableImageLazyLoad}
                                            cardNumber={1}
                                        />
                                    )}
                                />
                            ))}
                    {items.length > 1 && (
                        <StyledGridList>
                            {items.slice(1, 6).map((item, index) => {
                                const cardNumber = index + 2
                                return (
                                    <CardSwitcher
                                        key={index}
                                        item={item}
                                        cardContext="oscar-list"
                                        cardNumber={cardNumber}
                                        publicationCard={publicationItem => (
                                            <ListCard
                                                hideTopic
                                                item={publicationItem}
                                                onEvent={onEvent}
                                                simpleList={true}
                                                noStretch={true}
                                                removeHorizontalMargin={removeMargins}
                                                disableImageLazyLoad={disableImageLazyLoad}
                                                cardNumber={cardNumber}
                                                isTruncated={truncateListCards}
                                                hasSeparators={hasSeparators}
                                            />
                                        )}
                                    />
                                )
                            })}
                        </StyledGridList>
                    )}
                </StyledOscar>
            )}
        </ImpressionAvailable>
    )

    return section ? (
        <OverrideThemeSection section={section}>{renderOscar()}</OverrideThemeSection>
    ) : (
        renderOscar()
    )
}
Oscar.displayName = 'Oscar'
